import { Box, Group, Loader, Text } from '@mantine/core';
import {
  IconArchive,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react';

import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { getRecordRange } from 'src/design/molecules/recordRange';

export interface IArchiveNavigatorProps {
  /** Current page number (1-based) */
  page: number;
  /** Total number of pages */
  totalPages: number;
  /** Total count of all records across all pages */
  totalCount: number;
  /** Number of records per page */
  pageSize: number;
  /** Whether data is currently loading */
  loading?: boolean;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Optional: Date range string for the current archive (e.g., "Dec 2024 - Jan 2025") */
  dateRange?: string;
}

/** @registry Prev/next navigator between fixed-size archive "pages" (not row-level pagination) with a record-range summary; use for coarse date-bucketed archives. */
export const ArchiveNavigator: React.FC<IArchiveNavigatorProps> = ({
  page,
  totalPages,
  totalCount,
  pageSize,
  loading = false,
  onPageChange,
  dateRange,
}) => {
  const { startRecord, endRecord, canGoPrevious, canGoNext } = getRecordRange({
    page,
    pageSize,
    totalCount,
    totalPages,
  });

  // Hide only when there are no records at all
  if (totalCount === 0 && !loading) {
    return null;
  }

  return (
    <Box py="md" px="lg">
      <Group justify="center" gap="xl">
        <BasicActionIcon
          label={canGoPrevious ? 'View newer records' : 'Viewing most recent'}
          tooltipProps={{ position: 'bottom' }}
          variant="subtle"
          color="dark"
          size="xl"
          radius="xl"
          onClick={() => onPageChange(page - 1)}
          disabled={!canGoPrevious || loading}
          style={{
            transition: 'all 0.2s ease',
            opacity: canGoPrevious && !loading ? 1 : 0.3,
          }}
        >
          <IconChevronLeft size={24} />
        </BasicActionIcon>

        <Group gap="md" align="center">
          <Box
            p="sm"
            bg="blue.6"
            style={{
              borderRadius: 'var(--mantine-radius-md)',
              boxShadow: 'var(--mantine-shadow-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {loading ? (
              <Loader size={20} color="white" />
            ) : (
              <IconArchive size={20} color="white" />
            )}
          </Box>

          <Box style={{ textAlign: 'center', minWidth: 200 }}>
            <Text size="lg" fw={600} c="bright">
              Archive {page} of {totalPages || 1}
            </Text>
            {dateRange && (
              <Text size="sm" c="dimmed" mt={2}>
                {dateRange}
              </Text>
            )}
            <Text size="xs" c="dimmed" mt={4}>
              {totalCount === 0
                ? 'No records'
                : `Records ${startRecord.toLocaleString()} - ${endRecord.toLocaleString()} of ${totalCount.toLocaleString()}`}
            </Text>
          </Box>
        </Group>

        <BasicActionIcon
          label={canGoNext ? 'View older records' : 'Viewing oldest records'}
          tooltipProps={{ position: 'bottom' }}
          variant="subtle"
          color="dark"
          size="xl"
          radius="xl"
          onClick={() => onPageChange(page + 1)}
          disabled={!canGoNext || loading}
          style={{
            transition: 'all 0.2s ease',
            opacity: canGoNext && !loading ? 1 : 0.3,
          }}
        >
          <IconChevronRight size={24} />
        </BasicActionIcon>
      </Group>
    </Box>
  );
};
